import { faArrowLeft } from '@fortawesome/pro-light-svg-icons/faArrowLeft';
import { faCheck, faExclamation, faXmark } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Typography } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { AppointmentLog } from '@spike/appointment-model';
import { AppointmentsStatus, getAppointmentHistoryThunk } from '@spike/bookings-action';
import clsx from 'clsx';
import SidePanel from 'components/UI/SidePanel';
import Spinner from 'components/UI/V3/Spinner';
import { useApiClientWrapper } from 'hooks';
import useNonInitialEffect from '@versiondos/hooks';
import useTimeZone from 'hooks/useTimeZone';
import moment from 'moment-timezone';
import { Staff } from 'model/Staff';
import { fetchStaffThunk } from 'actions/staff/StaffActions';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import styles from './BookingActivityLog.module.scss';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            display: 'flex',
            flex: 1,
            flexDirection: 'column',
            height: '100%'
        },
        headerContainer: {
            paddingTop: 37,
            paddingBottom: 18,
            paddingLeft: 32,
            paddingRight: 32,
            [theme.breakpoints.down('sm')]: {
                paddingTop: 24,
                paddingBottom: 16,
                paddingLeft: 16,
                paddingRight: 16
            }
        },
        header: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
        },
        titleContainer: {
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer'
        },
        title: {
            fontWeight: 600,
            fontSize: 20,
            lineHeight: '20px',
            [theme.breakpoints.up('md')]: {
                fontSize: 22,
                lineHeight: '22px'
            }
        },
        icon: {
            cursor: 'pointer',
            fontSize: 22,
            [theme.breakpoints.up('md')]: {
                fontSize: 24
            }
        },
        backIcon: {
            paddingRight: 16,
            [theme.breakpoints.up('md')]: {
                paddingRight: 20
            }
        }
    })
);

type EntryStatus = 'success' | 'destructive' | 'warning';

interface BookingActivityLogProps {
    bookingId: number;
    onClose: () => void;
    onPanelClose: () => void;
    zIndex?: number;
}

const REMOVED_FOR_PATTERN = /removed .+ for .+/i;

const inferEntryStatus = (description: string): EntryStatus => {
    const lower = description.toLowerCase();

    if (lower.includes('canceled') || lower.includes('cancelled')) {
        return 'warning';
    }

    if (REMOVED_FOR_PATTERN.test(description)) {
        return 'destructive';
    }

    return 'success';
};

const getStatusIcon = (status: EntryStatus) => {
    switch (status) {
        case 'destructive':
            return faXmark;
        case 'warning':
            return faExclamation;
        default:
            return faCheck;
    }
};

const parseDescription = (text: string): React.ReactNode => {
    const parts = text.split(/(<strong>[^<]*<\/strong>)/g);

    if (parts.length === 1) return text;

    return parts.map((part, index) => {
        if (part.startsWith('<strong>') && part.endsWith('</strong>')) {
            return <strong key={index}>{part.slice(8, -9)}</strong>;
        }
        return <React.Fragment key={index}>{part}</React.Fragment>;
    });
};

const formatTimestamp = (date: moment.Moment | undefined, timeZone: string): string => {
    if (!date) return '';
    return date.tz(timeZone).format('MMM D, YYYY - h:mm a');
};

interface BookingActivityLogEntryProps {
    log: AppointmentLog;
    avatarUrl?: string;
    isLast: boolean;
    timeZone: string;
}

const BookingActivityLogEntry: React.FC<BookingActivityLogEntryProps> = ({ log, avatarUrl, isLast, timeZone }) => {
    const status = inferEntryStatus(log.logDescription);

    return (
        <div className={styles.entry}>
            <div className={styles.iconColumn}>
                <div className={clsx(styles.statusIcon, styles[status])}>
                    <FontAwesomeIcon icon={getStatusIcon(status)} />
                </div>
                {!isLast && <div className={styles.timelineLine} />}
            </div>

            <div className={styles.entryContent}>
                {avatarUrl ? (
                    <img
                        src={avatarUrl}
                        alt=""
                        className={styles.avatar}
                    />
                ) : (
                    <div className={styles.avatarPlaceholder} />
                )}

                <div className={styles.textContent}>
                    <div className={styles.description}>{parseDescription(log.logDescription)}</div>
                    <div className={styles.timestamp}>{formatTimestamp(log.createdAt, timeZone)}</div>
                </div>
            </div>
        </div>
    );
};

export const BookingActivityLog: React.FC<BookingActivityLogProps> = ({ bookingId, onClose, onPanelClose, zIndex }) => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();
    const timeZone = useTimeZone();

    const logs = useSelector<RootState, Array<AppointmentLog>>(state => state.appointments.logs);
    const appointmentsStatus = useSelector<RootState, AppointmentsStatus>(state => state.appointments.status);
    const staffList = useSelector<RootState, Array<Staff>>(state => state.staff.staff);

    const staffAvatarById = useMemo(
        () => new Map(staffList.filter(s => s.id != null && s.person.avatar).map(s => [s.id!, s.person.avatar])),
        [staffList]
    );

    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);

    useEffect(() => {
        dispatch(fetchStaffThunk());
    }, []);

    useEffect(() => {
        setIsLoading(true);
        setHasError(false);
        dispatch(getAppointmentHistoryThunk(apiClientWrapper, bookingId));
    }, [bookingId]);

    useNonInitialEffect(() => {
        switch (appointmentsStatus) {
            case AppointmentsStatus.GettingHistory:
                setIsLoading(true);
                break;
            case AppointmentsStatus.GetHistorySuccess:
                setIsLoading(false);
                setHasError(false);
                break;
            case AppointmentsStatus.Error:
                setIsLoading(false);
                setHasError(true);
                break;
        }
    }, [appointmentsStatus]);

    return (
        <SidePanel
            side="right"
            onClose={onPanelClose}
            zIndex={zIndex}
        >
            <Box className={classes.container}>
                <Box className={classes.headerContainer}>
                    <Box className={classes.header}>
                        <Box className={classes.titleContainer}>
                            <FontAwesomeIcon
                                icon={faArrowLeft}
                                className={clsx(classes.icon, classes.backIcon)}
                                onClick={onClose}
                            />
                            <Typography className={classes.title}>Appointment Log</Typography>
                        </Box>
                    </Box>
                </Box>

                <div className={styles.content}>
                    {isLoading ? (
                        <div className={styles.loadingContainer}>
                            <Spinner />
                        </div>
                    ) : hasError ? (
                        <div className={styles.emptyState}>Unable to load activity log</div>
                    ) : logs.length === 0 ? (
                        <div className={styles.emptyState}>No activity recorded yet</div>
                    ) : (
                        <div className={styles.entries}>
                            {logs.map((log, index) => (
                                <BookingActivityLogEntry
                                    key={log.id}
                                    log={log}
                                    avatarUrl={log.staffId != null ? staffAvatarById.get(log.staffId) : undefined}
                                    isLast={index === logs.length - 1}
                                    timeZone={timeZone}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </Box>
        </SidePanel>
    );
};

export default BookingActivityLog;
